import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Input,
  Textarea,
  Select,
  SimpleGrid,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Text
} from "@chakra-ui/react";
import { createClient, updateClient, listUsersInMyBusinessUnit } from "../../../api/clientsApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { useTranslation } from "react-i18next";
import { FieldLabel } from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX
} from "../../../components/Portal/portalModalChrome";

const EMPTY_FORM = {
  name: "",
  description: "",
  sector: "",
  location: "",
  manager_id: "",
  number_of_employees: "",
  linkedin_url: ""
};

const ClientFormModal = ({ isOpen, onClose, initialData, onSuccess }) => {
  const { selectedBusinessUnitId } = useBusinessUnit();
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const duplicateCancelRef = useRef();
  const toast = useToast();
  const { t } = useTranslation("portal");

  const isEdit = Boolean(initialData?.id);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      const employeesValue =
        initialData?.number_of_employees ?? initialData?.numberOfEmployees;
      setForm({
        name: initialData?.name || "",
        description: initialData?.description || "",
        sector: initialData?.sector || "",
        location: initialData?.location || "",
        manager_id: initialData?.manager_id || "",
        number_of_employees:
          employeesValue != null ? String(employeesValue) : "",
        linkedin_url:
          initialData?.linkedin_url || initialData?.linkedinUrl || ""
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [initialData, isEdit, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    listUsersInMyBusinessUnit({ buId: selectedBusinessUnitId || undefined }).then(setUsers).catch(() => {
      setUsers([]);
    });
  }, [isOpen, selectedBusinessUnitId]);

  const isValid = useMemo(() => {
    return form.name.trim().length > 0;
  }, [form.name]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      const trimmedEmployees = String(form.number_of_employees || "").trim();
      const parsedEmployees =
        trimmedEmployees.length > 0 ? Number(trimmedEmployees) : undefined;
      const payload = {
        ...form,
        description: form.description.trim() || undefined,
        sector: form.sector.trim() || undefined,
        location: form.location.trim() || undefined,
        manager_id: form.manager_id ? Number(form.manager_id) : undefined,
        number_of_employees:
          Number.isFinite(parsedEmployees) ? parsedEmployees : undefined,
        linkedin_url: form.linkedin_url.trim() || undefined
      };

      if (isEdit) {
        const updated = await updateClient(initialData.id, payload, { buId: selectedBusinessUnitId || undefined });
        toast({ title: t("pilotClients.form.toasts.updated"), status: "success" });
        onSuccess?.(updated);
      } else {
        const created = await createClient(payload, { buId: selectedBusinessUnitId || undefined });
        toast({ title: t("pilotClients.form.toasts.created"), status: "success" });
        onSuccess?.(created);
      }
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.duplicate) {
        setDuplicateInfo(detail);
      } else {
        toast({
          title: t("pilotClients.form.toasts.operationFailedTitle"),
          description: e?.response?.data?.message || detail?.code || t("pilotClients.form.toasts.operationFailedDescription"),
          status: "error"
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <ModalOverlay bg="rgba(11,31,58,.45)" />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX}>
          {isEdit ? t("pilotClients.form.modalTitleEdit") : t("pilotClients.form.modalTitleCreate")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py="16px">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="16px" spacingY="12px">
            <Box>
              <FieldLabel isRequired>{t("pilotClients.form.fields.name")}</FieldLabel>
              <Input
                size="sm"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder={t("pilotClients.form.placeholders.name")}
                isRequired
                aria-required={true}
              />
            </Box>
            <Box>
              <FieldLabel>{t("pilotClients.form.fields.sector")}</FieldLabel>
              <Input
                size="sm"
                name="sector"
                value={form.sector}
                onChange={handleChange}
                placeholder={t("pilotClients.form.placeholders.sector")}
              />
            </Box>
            <Box>
              <FieldLabel>{t("pilotClients.form.fields.location")}</FieldLabel>
              <Input
                size="sm"
                name="location"
                value={form.location}
                onChange={handleChange}
                placeholder={t("pilotClients.form.placeholders.location")}
              />
            </Box>
            <Box>
              <FieldLabel>{t("pilotClients.form.fields.numberOfEmployees")}</FieldLabel>
              <Input
                size="sm"
                name="number_of_employees"
                type="number"
                min={0}
                value={form.number_of_employees}
                onChange={handleChange}
                placeholder={t("pilotClients.form.placeholders.numberOfEmployees")}
              />
            </Box>
            <Box>
              <FieldLabel>{t("pilotClients.form.fields.manager")}</FieldLabel>
              <Select
                size="sm"
                name="manager_id"
                value={form.manager_id || ""}
                onChange={handleChange}
                placeholder={t("pilotClients.form.fields.manager")}
              >
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} - {u.email}
                  </option>
                ))}
              </Select>
            </Box>
            <Box>
              <FieldLabel>{t("pilotClients.form.fields.linkedinUrl")}</FieldLabel>
              <Input
                size="sm"
                name="linkedin_url"
                type="url"
                value={form.linkedin_url}
                onChange={handleChange}
                placeholder={t("pilotClients.form.placeholders.linkedinUrl")}
              />
            </Box>
          </SimpleGrid>
          <Box mt="12px">
            <FieldLabel>{t("pilotClients.form.fields.description")}</FieldLabel>
            <Textarea
              size="sm"
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder={t("pilotClients.form.placeholders.description")}
            />
          </Box>
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t("pilotClients.form.buttons.cancel")}
          </Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} isDisabled={!isValid} isLoading={isSubmitting}>
            {isEdit ? t("pilotClients.form.buttons.saveChanges") : t("pilotClients.form.buttons.create")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {/* Duplicate company warning dialog */}
    <AlertDialog
      isOpen={Boolean(duplicateInfo)}
      leastDestructiveRef={duplicateCancelRef}
      onClose={() => setDuplicateInfo(null)}
    >
      <AlertDialogOverlay bg="rgba(11,31,58,.45)">
        <AlertDialogContent sx={MODAL_CONTENT_SX}>
          <AlertDialogHeader sx={MODAL_HEADER_SX}>
            {t("pilotClients.form.duplicate.title", { defaultValue: "Duplicate company" })}
          </AlertDialogHeader>
          <AlertDialogBody py="16px">
            <Text fontSize="13px" color="portal.text" sx={{ marginBottom: "8px !important" }}>
              {t("pilotClients.form.duplicate.body", {
                name: duplicateInfo?.existingName,
                defaultValue: `A company named "${duplicateInfo?.existingName}" already exists.`
              })}
            </Text>
            <Text fontSize="13px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
              {t("pilotClients.form.duplicate.prompt", { defaultValue: "Would you like to view the existing company?" })}
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter sx={MODAL_FOOTER_SX}>
            <Button ref={duplicateCancelRef} variant="secondary" size="sm" onClick={() => setDuplicateInfo(null)}>
              {t("pilotClients.form.duplicate.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                const info = duplicateInfo;
                setDuplicateInfo(null);
                onClose?.();
                if (info?.existingId) {
                  onSuccess?.({ id: info.existingId, _viewExisting: true });
                }
              }}
            >
              {t("pilotClients.form.duplicate.view", { defaultValue: "View existing" })}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
    </>
  );
};

export default ClientFormModal;
